import React, { useCallback, useEffect, useMemo, useState } from 'react'

import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Dialog, Typography } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import IconButton from '@material-ui/core/IconButton'
import { Theme, makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import { getUserObject } from 'api/graphQL/reactiveVars/usersReactiveVar'
import { AddNotesModal } from './AddNotesModal'
import { ListingNotesCard } from './ListingNotesCard'
import PDNColors from './PDNColors'
import {
    BUTTON_ADD_NEW,
    LABEL_CREATE_NOTES_1,
    LABEL_CREATE_NOTES_2,
    LABEL_NOTES,
    VARIABLE_NOTES_COUNT,
    noteTypes,
} from './PDNConstants'
import { ProductDetailNoteData } from './PDNTypes'
import EmptyNotes from './assets/EmptyNotes'
import NotesArrow from './assets/NotesArrow'
import NotesFilters from './filters/NotesFilters'
import usePDNFiltersHook from './hooks/usePDNFiltersHook'
import usePDNhook from './hooks/usePDNHook'

const {
    deltekNoteColor,
    deltekNoteHoverColor,
    dialogActionHR,
    customerNoteColor,
    viewOnlyBackgroundColor,
    viewOnlyHeaderColor,
} = PDNColors

export type ListingNotesModalStyleProps = {
    headerColor?: string
    isViewOnly: boolean
}

const useStyles = makeStyles((theme: Theme) => ({
    dialog: ({ isViewOnly }: ListingNotesModalStyleProps) => ({
        '& .MuiDialog-paperScrollPaper': {
            backgroundColor: !isViewOnly
                ? theme.palette.common.white
                : viewOnlyBackgroundColor,
            color: !isViewOnly
                ? viewOnlyHeaderColor
                : theme.palette.common.white,
            padding: theme.spacing(1.5),
        },
    }),
    dialogContent: {
        display: 'flex',
        minHeight: pxToRem(400),
        flexDirection: 'column',
        color: ({ isViewOnly }: ListingNotesModalStyleProps) =>
            !isViewOnly
                ? theme.palette.common.black
                : theme.palette.common.white,
        fontSize: pxToRem(18),
        padding: theme.spacing(1, 3, 3, 1.5),
        overflowY: 'auto',
        backgroundColor: 'inherit',
    },
    actionButton: {
        backgroundColor: deltekNoteColor,
        width: 'auto',
        minWidth: pxToRem(100),
        height: pxToRem(41),
        fontSize: pxToRem(18),
        color: theme.palette.common.white,
        borderRadius: 100,
        '&:hover': {
            backgroundColor: deltekNoteHoverColor,
        },
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(2),
        },
    },
    detailText: {
        fontSize: pxToRem(11),
    },
    circularProgressCenter: {
        position: 'absolute',
        left: '50%',
        top: '50%',
    },
    emptyNotesContainer: {
        display: 'flex',
        width: '70%',
        margin: '0 auto',
        flexDirection: 'column',
        alignItems: 'center',
    },
    emptyNotesArrow: {
        display: 'flex',
        alignContent: 'flex-end',
        justifyContent: 'flex-end',
        width: '100%',
    },
    emptyNotesDescription: {
        color: customerNoteColor,
        fontWeight: 'bold',
    },
    closeIcon: {
        color: ({ isViewOnly }: ListingNotesModalStyleProps) =>
            !isViewOnly
                ? theme.palette.common.black
                : theme.palette.common.white,
        position: 'absolute',
        right: 0,
    },
    filterContainer: {
        padding: theme.spacing(0),
        maxWidth: '120rem',
        width: '80vw',
        margin: theme.spacing(1, 'auto'),
    },
    headerContainer: {
        position: 'relative',
        fontSize: pxToRem(24),
        padding: theme.spacing(3, 0, 1, 0),
        maxWidth: '120rem',
        width: '80vw',
        margin: theme.spacing(0, 'auto'),
        right: theme.spacing(1),
    },
    closeIconContainer: {
        position: 'absolute',
        right: 0,
    },
    noResults: {
        display: 'flex',
        margin: theme.spacing(3, 'auto'),
    },
    notesHeaderContainer: {
        fontSize: theme.spacing(3),
    },
    notesHeader: {
        fontSize: 'inherit',
        fontWeight: 'bold',
    },
    horizontalRule: {
        position: 'relative',
        borderBottom: `2px solid ${dialogActionHR}`,
        width: '80vw',
        right: theme.spacing(1),
        maxWidth: '120rem',
        margin: theme.spacing(0, 'auto'),
    },
}))

export interface ListingNotesModalProps {
    open: boolean
    onClose: () => void
    isOnEditMode: boolean
    isManufacturerListing: boolean
}

const ListingNotesModal: React.FC<ListingNotesModalProps> = ({
    open,
    onClose,
    isOnEditMode,
    isManufacturerListing,
}) => {
    const {
        currentUser,
        editableNote,
        handleEditNote,
        handleCancelEditNote,
        handleDeleteNote,
        handleSaveNote,
        pdnData,
    } = usePDNhook()

    const { clearFilters, filters: notesFilter } = usePDNFiltersHook()

    const isDeltekPublishingAuthor = useMemo(
        () => currentUser.role === 'PublishingAuthor',
        [currentUser]
    )

    const isViewOnly = useMemo(() => {
        return !(
            isOnEditMode ||
            (isDeltekPublishingAuthor && isManufacturerListing)
        )
    }, [isDeltekPublishingAuthor, isOnEditMode, isManufacturerListing])

    const classes = useStyles({
        isViewOnly,
    })

    const [addNotesModalOpen, setAddNotesModalOpen] = useState(false)

    const filteredNotes = useMemo(() => {
        let filteredData = [...pdnData]

        if (notesFilter.users?.length) {
            filteredData = filteredData.filter(
                (note: Partial<ProductDetailNoteData>) =>
                    notesFilter.users.includes(
                        getUserObject(note?.userId ?? '').fullName ?? ''
                    )
            )
        }

        if (notesFilter.noteTypes?.length) {
            filteredData = filteredData.filter(
                (note: Partial<ProductDetailNoteData>) =>
                    !!notesFilter.noteTypes.find(
                        ({ reason }) => reason === note.reason
                    )
            )
        }

        if (!!notesFilter.dateFrom || !!notesFilter.dateTo) {
            const dateFrom = !!notesFilter.dateFrom
                ? new Date(notesFilter.dateFrom).getTime()
                : new Date('Jan 1, 1970').getTime()

            const dateTo = !!notesFilter.dateTo
                ? new Date(`${notesFilter.dateTo} 23:59:59`).getTime()
                : new Date().getTime()

            filteredData = filteredData.filter((note) => {
                const checkTime = new Date(note.createdDate ?? '').getTime()
                return checkTime < dateTo && checkTime >= dateFrom
            })
        }

        return filteredData.sort(
            (a, b) =>
                new Date(b.createdDate ?? '').getTime() -
                new Date(a.createdDate ?? '').getTime()
        )
    }, [pdnData, notesFilter])

    const pdnNoteTypes = useMemo(() => {
        return [
            ...new Set(
                pdnData
                    .sort(
                        (
                            a: Partial<ProductDetailNoteData>,
                            b: Partial<ProductDetailNoteData>
                        ) => {
                            if (a.noteType! > b.noteType!) {
                                return 1
                            }
                            if (a.noteType! < b.noteType!) {
                                return -1
                            }
                            return 0
                        }
                    )
                    .map(
                        ({
                            noteType,
                            reason,
                        }: Partial<ProductDetailNoteData>) =>
                            JSON.stringify({
                                noteType:
                                    noteTypes[
                                        noteType as keyof typeof noteTypes
                                    ],
                                reason,
                            })
                    )
            ),
        ].map((stringifiedOptions) => JSON.parse(stringifiedOptions))
    }, [pdnData, noteTypes])

    const hasFilters = useMemo(
        () =>
            !!notesFilter.users.length ||
            !!notesFilter.noteTypes.length ||
            !!notesFilter.dateFrom ||
            !!notesFilter.dateTo,
        [notesFilter]
    )

    const noteToEdit = useMemo(() => {
        if (editableNote) {
            return pdnData?.[
                pdnData?.findIndex(
                    (note: Partial<ProductDetailNoteData>) =>
                        note?.id === editableNote
                )
            ]
        }
        return undefined
    }, [pdnData, editableNote])

    const handleOpenAddNotes = useCallback(() => setAddNotesModalOpen(true), [])
    const handleCancel = useCallback(() => {
        setAddNotesModalOpen(false)
        handleCancelEditNote?.()
        clearFilters?.()
    }, [handleCancelEditNote, clearFilters])

    useEffect(() => {
        if (!!editableNote && !addNotesModalOpen) {
            setAddNotesModalOpen(true)
        }
    }, [addNotesModalOpen, editableNote])

    return (
        <>
            <Dialog
                open={open}
                fullWidth
                maxWidth="lg"
                data-testid="listingNotesModal"
                className={classes.dialog}
                aria-labelledby="listing-notes"
            >
                <GridContainer className={classes.headerContainer}>
                    <IconButton
                        onClick={onClose}
                        className={classes.closeIconContainer}
                    >
                        <CloseIcon className={classes.closeIcon} />
                    </IconButton>
                    <Typography
                        component="span"
                        className={classes.notesHeaderContainer}
                    >
                        <Typography
                            component="span"
                            className={classes.notesHeader}
                        >
                            {LABEL_NOTES}{' '}
                        </Typography>

                        {VARIABLE_NOTES_COUNT(filteredNotes?.length ?? 0)}
                    </Typography>
                </GridContainer>

                <DialogContent className={classes.dialogContent}>
                    <>
                        <NotesFilters noteTypes={pdnNoteTypes ?? []} />
                        {!!filteredNotes?.length ? (
                            filteredNotes?.map(
                                (
                                    item: Partial<ProductDetailNoteData>,
                                    index: number
                                ) => (
                                    <ListingNotesCard
                                        mode={!isViewOnly ? 'edit' : 'display'}
                                        key={`${index}-${item.id}`}
                                        note={item}
                                        onEdit={handleEditNote}
                                        onDelete={handleDeleteNote}
                                        currentUserId={currentUser?.id}
                                        enableEdit={isDeltekPublishingAuthor}
                                    />
                                )
                            )
                        ) : !!hasFilters ? (
                            <Typography
                                component="div"
                                align="center"
                                className={classes.noResults}
                            >
                                <h3> No Results </h3>
                            </Typography>
                        ) : (
                            <Typography
                                component="span"
                                className={classes.emptyNotesContainer}
                            >
                                <EmptyNotes />
                                <Typography
                                    component="span"
                                    className={classes.emptyNotesDescription}
                                >
                                    {LABEL_CREATE_NOTES_1}
                                </Typography>
                                {!isViewOnly && (
                                    <>
                                        <Typography
                                            component="span"
                                            className={
                                                classes.emptyNotesDescription
                                            }
                                        >
                                            {LABEL_CREATE_NOTES_2}
                                        </Typography>
                                        <Typography
                                            className={classes.emptyNotesArrow}
                                            component="span"
                                        >
                                            <NotesArrow />
                                        </Typography>
                                    </>
                                )}
                            </Typography>
                        )}
                    </>
                </DialogContent>
                {!isViewOnly && (
                    <>
                        <Typography
                            component="div"
                            className={classes.horizontalRule}
                        />
                        <DialogActions className={classes.dialogAction}>
                            <Button
                                data-testid="btnAddNew"
                                className={classes.actionButton}
                                onClick={handleOpenAddNotes}
                            >
                                {BUTTON_ADD_NEW}
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
            {!!addNotesModalOpen && (
                <AddNotesModal
                    open={addNotesModalOpen}
                    onSave={handleSaveNote}
                    onCancel={handleCancel}
                    isEditMode={!isViewOnly}
                    noteType={noteToEdit?.noteType}
                    note={noteToEdit}
                    action={noteToEdit?.noteType}
                />
            )}
        </>
    )
}

export default ListingNotesModal
